/**
 * Turns a complex object with nested objects and arrays into
 * a single-level object with keys matching our `gives` notation.
 * @param obj The object to be flattened
 * @param parent The parent key (optional)
 * @returns The flattened object
 */
function flatten(
  obj: Record<string, any> | any[],
  parent = ''
): Record<string, any> {
  return Object.entries(obj).reduce((flat, [key, value]) => {
    let childKey = parent ? `${parent}__${key}` : key;
    if (Array.isArray(value)) {
      if (
        value.some(
          (v) => (typeof v === 'object' && v !== null) || Array.isArray(v)
        )
      ) {
        childKey += '[]';
        return {
          ...flat,
          ...value
            .map((subObj) => flatten(subObj))
            .reduce(function toSingleObject(singleObj, subObj) {
              const nextSingleObj = { ...singleObj };
              Object.entries(subObj).forEach(([k, v]) => {
                nextSingleObj[`${childKey}${k}`] = [
                  ...(Array.isArray(singleObj[`${childKey}${k}`])
                    ? singleObj[`${childKey}${k}`]
                    : []),
                  v,
                ];
              });
              return nextSingleObj;
            }, {} as Record<string, any>),
        };
      } else {
        return {
          ...flat,
          [childKey]: value, // array of simple values (string, number, boolean)
        };
      }
    }

    if (typeof value === 'object' && value !== null) {
      return {
        ...flat,
        ...flatten(value, childKey),
      };
    }

    return { ...flat, [childKey]: value };
  }, {});
}

export default flatten;
